import React, { useMemo, useState } from 'react';
import { AvTag, TextInput } from '@fabric-ui/components';
import { Search } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from '@utils/AvRouter';
import { calculatePercentageChange } from '../utils';
import AgentCategoryTag from './AgentCategoryTag';
import { getAlertsByAgent, getAllAgents } from './queries';
import StatusChange from './statusChange';
import { Agent, AgentDetailsFilterProps } from './types';
import { AGENT_TILE_SKELETON_ARRAY, agentDescriptionStyle, agentWidgetBoxStyle, getLast24HoursDate } from './utils';

const AgentTiles: React.FC<AgentDetailsFilterProps> = ({ filterContext }) => {
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  const alertsByAgentQuery = useMemo(() => [getAlertsByAgent(filterContext)], [filterContext]);

  const { data: totalAlertsByAgents, isLoading: isTotalAlertsByAgentsDataLoading } = useQueryObject({
    queryObject: alertsByAgentQuery,
    options: { placeholderData: keepPreviousData },
  });

  const alertsByAgent24hQuery = useMemo(
    () => [
      getAlertsByAgent({
        ...filterContext,
        dateRange: [getLast24HoursDate(filterContext.dateRange[1]), filterContext.dateRange[1]],
      }),
    ],
    [filterContext]
  );

  const { data: totalAlertsByAgentsInLast24Hours, isLoading: isTotalAlertsByAgentsInLast24HoursDataLoading } = useQueryObject({
    queryObject: alertsByAgent24hQuery,
    options: { placeholderData: keepPreviousData },
  });

  const totalAlertsByAgentsInLast24HoursMap = useMemo(() => {
    if (!isTotalAlertsByAgentsInLast24HoursDataLoading) {
      return new Map(totalAlertsByAgentsInLast24Hours?.map(a => [a.agent_id, a.cnt]));
    }
    return new Map();
  }, [totalAlertsByAgentsInLast24Hours, isTotalAlertsByAgentsInLast24HoursDataLoading]);

  const allAgentsQuery = useMemo(() => [getAllAgents], []);

  const { data: totalAgents, isLoading: isTotalAgentsDataLoading } = useQueryObject({
    queryObject: allAgentsQuery,
    options: { placeholderData: keepPreviousData },
  });

  const AgentTilesData = useMemo(() => {
    if (isTotalAgentsDataLoading || isTotalAlertsByAgentsDataLoading) return [];

    const agentDataMap = new Map(totalAlertsByAgents?.map(a => [a.agent_id, a]));

    return (totalAgents || [])
      .map((agent: Agent) => {
        const alertData = agentDataMap.get(agent.agent_id) as Agent | undefined;
        return {
          ...agent,
          cnt: alertData?.cnt ?? 0,
          outcome: alertData?.outcome ?? [],
          primaryOutcome: alertData?.primaryOutcome ?? [],
          secondaryOutcome: alertData?.secondaryOutcome ?? [],
          actions: alertData?.actions ?? [],
        };
      })
      .sort((a, b) => b.cnt - a.cnt);
  }, [totalAgents, totalAlertsByAgents, isTotalAgentsDataLoading, isTotalAlertsByAgentsDataLoading]);

  const onTileClick = ({ agent_id, name, description, category, outcome, cnt, actions, primaryOutcome, secondaryOutcome }) =>
    navigate({
      from: '/',
      to: `/$accountId/threats/agents/${agent_id}`,
      state: {
        name,
        description,
        category,
        outcome,
        count: cnt,
        actions,
        primaryOutcome,
        secondaryOutcome,
      },
    });

  const filteredAgentTiles = useMemo(() => {
    const agentNameFilters = filterContext.filters['agents.name'] || [];
    const agentCategoryFilters = filterContext.filters['agents.category'] || [];

    const searchLower = searchValue.toLowerCase();
    const filterSet = new Set(agentNameFilters.map((f: string) => f.toLowerCase()));
    const filterCategorySet = new Set(agentCategoryFilters.map((f: string) => f.toLowerCase()));

    return AgentTilesData.filter(agent => {
      const name = agent.name?.toLowerCase() || '';
      const category = agent.category?.toLowerCase() || '';

      if (searchLower && !name.includes(searchLower)) return false;
      if (filterSet.size > 0 && !filterSet.has(name)) return false;
      if (filterCategorySet.size > 0 && !filterCategorySet.has(category)) return false;

      return true;
    });
  }, [AgentTilesData, filterContext, searchValue]);

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, mt: 1, color: 'var(--color-content-base-primary)' }}>
          Agent Fleet - Digital Workforce
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--color-content-base-secondary)' }}>
          Click on any agent to view detailed alert history and investigations
        </Typography>
        <TextInput
          size="small"
          helperText="Search Agent"
          icon={<Search />}
          value={searchValue}
          onChange={value => setSearchValue(value)}
          sx={{ width: '450px', mb: 2, mt: 2 }}
        />
      </Box>
      <Box sx={{ ...flex.row, flexWrap: 'wrap', gap: 2, width: '100%', pl: 0.5, mb: 3 }}>
        {!isTotalAgentsDataLoading && !isTotalAlertsByAgentsDataLoading && !isTotalAlertsByAgentsInLast24HoursDataLoading
          ? filteredAgentTiles.map(agentItem => (
              <Box sx={agentWidgetBoxStyle(agentItem.cnt)} key={agentItem.agent_id} onClick={() => onTileClick(agentItem)}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: '16px', minHeight: '74px', p: 2.5, pb: 1, color: 'var(--color-content-base-primary)' }}>
                  {agentItem.name}
                </Typography>
                <AgentCategoryTag category={agentItem.category} />
                <Divider sx={{ borderColor: 'var(--color-border-base-subdued)' }} />
                <Box sx={{ ...flex.row, p: 2.5, pt: 2, pb: 2, justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ color: 'var(--color-content-base-secondary)', fontWeight: 500 }}>
                    Alerts investigated
                  </Typography>
                  <Box sx={{ ...flex.row, gap: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '16px', color: 'var(--color-content-base-primary)' }}>
                      {Number(agentItem.cnt || 0).toLocaleString()}
                    </Typography>
                    {agentItem.cnt > 0 && (
                      <StatusChange
                        value={calculatePercentageChange(totalAlertsByAgentsInLast24HoursMap.get(agentItem.agent_id) || 0, agentItem.cnt)}
                      />
                    )}
                  </Box>
                </Box>
                <Divider sx={{ borderColor: 'var(--color-border-base-subdued)', margin: '0 20px' }} />
                <Typography variant="body2" sx={agentDescriptionStyle}>
                  {agentItem.description}
                </Typography>
                <Divider sx={{ borderColor: 'var(--color-border-base-subdued)', margin: '0 20px' }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5, p: 2.5, pt: 2, pb: 0 }}>
                  {[agentItem.primaryOutcome?.[0], agentItem.secondaryOutcome?.[0]].some(Boolean) && (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Outcome
                      </Typography>
                      <Box display="flex" gap={0.5}>
                        {[agentItem.primaryOutcome?.[0], agentItem.secondaryOutcome?.[0]].map(
                          // eslint-disable-next-line react/no-array-index-key
                          (outcome, idx) => outcome && <AvTag key={idx} variant="neutral" text={outcome} oneLine />
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))
          : AGENT_TILE_SKELETON_ARRAY.map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`agent-tile-${index}`} sx={agentWidgetBoxStyle(0)}>
                <Skeleton variant="rectangular" width="100%" height="250px" sx={{ borderRadius: '2px' }} />
              </Box>
            ))}
      </Box>
    </>
  );
};

export default AgentTiles;
